import React, { useState } from 'react';
import Modal from './Modal';
import { FaEnvelope, FaDownload, FaCheckCircle } from 'react-icons/fa';

const InvoiceModal = ({
  isOpen,
  onClose,
  onSendInvoice,
  onDownloadInvoice,
  paymentData,
  feeStructureId,
  generateInvoiceMutation,
  isLoading = false
}) => {
  const [emailSent, setEmailSent] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleSendInvoice = async () => {
    try {
      if (paymentData?._id || paymentData?.id) {
        await generateInvoiceMutation.mutateAsync(paymentData._id || paymentData.id);
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      if (paymentData?._id || paymentData?.id) {
        await generateInvoiceMutation.mutateAsync(paymentData._id || paymentData.id);
        setDownloadSuccess(true);
        setTimeout(() => {
          setDownloadSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  if (!paymentData) return null;



  // Format payment date
  const formatPaymentDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Payment Confirmation"
      type="success"
      size="lg"
    >
      <div className="space-y-6">
        {/* Success Message */}
        <div className="text-center">
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheckCircle className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            Payment Received Successfully!
          </h3>
          <p className="text-gray-300 text-sm">
            The payment has been recorded and verified. You can now send an invoice to the student.
          </p>
        </div>

        {/* Payment Details */}
        <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
          <h4 className="text-white font-medium mb-3">Payment Details</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-400">Student:</span>
              <span className="text-white ml-2">
                {paymentData.student?.first_name && paymentData.student?.last_name 
                  ? `${paymentData.student.first_name} ${paymentData.student.last_name}`
                  : paymentData.student?.first_name || paymentData.student?.last_name || paymentData.student_name || 'N/A'
                }
              </span>
            </div>
            <div>
              <span className="text-gray-400">Amount:</span>
              <span className="text-white ml-2">
                ₹{paymentData.amount ? paymentData.amount.toLocaleString('en-IN') : 0}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Payment Date:</span>
              <span className="text-white ml-2">{formatPaymentDate(paymentData.payment_date || paymentData.created_at)}</span>
            </div>
            <div>
              <span className="text-gray-400">Payment ID:</span>
              <span className="text-white ml-2">
                {paymentData.payment_id || (paymentData._id || paymentData.id || 'N/A').substring(0, 8)}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Batch:</span>
              <span className="text-white ml-2">
                {paymentData.batch?.batch_name || paymentData.batch_name || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Payment Method:</span>
              <span className="text-white ml-2">
                {paymentData.payment_method ? 
                  paymentData.payment_method.charAt(0).toUpperCase() + paymentData.payment_method.slice(1) 
                  : 'N/A'
                }
              </span>
            </div>
            <div>
              <span className="text-gray-400">Status:</span>
              <span className="text-white ml-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  paymentData.status === 'completed' 
                    ? 'bg-green-100 text-green-800' 
                    : paymentData.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {paymentData.status ? 
                    paymentData.status.charAt(0).toUpperCase() + paymentData.status.slice(1) 
                    : 'N/A'
                  }
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleSendInvoice}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200"
          >
            {emailSent ? (
              <>
                <FaCheckCircle className="text-green-400" />
                <span>Invoice Sent!</span>
              </>
            ) : (
              <>
                <FaEnvelope />
                <span>Send Invoice via Email</span>
              </>
            )}
          </button>

          <button
            onClick={handleDownloadInvoice}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200"
          >
            {downloadSuccess ? (
              <>
                <FaCheckCircle className="text-green-400" />
                <span>Downloaded!</span>
              </>
            ) : (
              <>
                <FaDownload />
                <span>Download Invoice</span>
              </>
            )}
          </button>
        </div>

        {/* Close Button */}
        <div className="text-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceModal; 